import React, { useEffect, useState } from "react";

import ChatPromptInput from "src/reusable/Chat/PromptInput/ChatPromptInput";
import { FileUploadState } from "src/reusable/types/fileUploadWithProgress";
import { NucToast } from "src/reusable/components/Toast";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { ImageContent } from "src/views/assistant/types";
import { type TokenUsageData } from "src/reusable/Chat/TokenUsageGauge";
import WorkflowChatInputButtons from "./WorkflowChatInputButtons";

type WorkflowChatInputProps = {
  chatStreamError: string | null;
  isChatConnectionError: boolean | null;
  isChatLoading: boolean;
  isTurnActive: boolean;
  terminateConnection: () => void;
  sendUserMessage: (text: string, images?: ImageContent[]) => Promise<boolean>;
  tokenUsage?: TokenUsageData | null;
  placeholder?: string;
  /** Backend product slug for the input's upload paths. */
  product: string;
};

/**
 * Chat-page composer for workflow products: the shared ChatPromptInput with
 * the persona selector in the buttons row and the standard uploaded-file
 * wiring into ChatStore (same behavior as the Parts AI input).
 */
const WorkflowChatInput = ({
  chatStreamError,
  isChatConnectionError,
  isChatLoading,
  isTurnActive,
  terminateConnection,
  sendUserMessage,
  tokenUsage,
  placeholder = "What can I help you with?",
  product,
}: WorkflowChatInputProps) => {
  const [userInput, setUserInput] = useState<string>("");

  const {
    uploadedFiles,
    addUploadedFile,
    removeUploadedFile,
    setSourceDataRecordIds,
  } = useChatStore();

  const handleFilesChange = (files: FileUploadState[]) => {
    // Clear existing user-uploaded files (but NOT private dataset docs or
    // project attachments).
    uploadedFiles.forEach((file) => {
      if (!file.isFromDialogueHistory && !file.isFromProject) {
        removeUploadedFile(file.id);
      }
    });

    files.forEach((file) => addUploadedFile(file));

    // Attachments add to the document scope, they do not define it: documents
    // chosen before the chat opened are not this composer's to drop.
    const sdrIds = files
      .map((file) => file.sourceDataRecordId)
      .filter((id): id is number => id !== undefined);
    const existing = useChatStore.getState().sourceDataRecordIds;
    setSourceDataRecordIds([...new Set([...existing, ...sdrIds])]);
  };

  // The per-file removal signal is what makes the add-only merge above safe:
  // it is the only place the composer knows an id was its own contribution.
  const handleRemoveFile = (file: FileUploadState) => {
    if (file.sourceDataRecordId === undefined) {
      return;
    }
    const existing = useChatStore.getState().sourceDataRecordIds;
    setSourceDataRecordIds(
      existing.filter((id) => id !== file.sourceDataRecordId),
    );
  };

  const handleFormSubmit = async () => {
    if (isTurnActive || isChatLoading) {
      return;
    }
    if (userInput.trim() === "" && uploadedFiles.length === 0) {
      NucToast({
        message: "Please enter a message or attach files",
        type: "error",
        hideProgressBar: false,
      });
      return;
    }
    try {
      const sentSuccessfully = await sendUserMessage(userInput);
      if (sentSuccessfully) {
        setUserInput("");
      }
    } catch (error) {
      console.error("[WorkflowChatInput] Failed to send message:", error);
      NucToast({
        message: "Failed to send message. Please try again.",
        type: "error",
        hideProgressBar: false,
      });
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserInput(event.target.value);
  };

  useEffect(() => {
    if (chatStreamError || isChatConnectionError) {
      terminateConnection();
    }
  }, [chatStreamError, terminateConnection, isChatConnectionError]);

  return (
    <ChatPromptInput
      onSubmit={handleFormSubmit}
      isInputting={!isTurnActive && userInput !== ""}
      isChatLoading={isChatLoading}
      isGenerating={isTurnActive}
      onStopChat={terminateConnection}
      value={userInput}
      onChange={handleChange}
      onFilesChange={handleFilesChange}
      onRemoveFile={handleRemoveFile}
      InputButtons={WorkflowChatInputButtons}
      placeholder={placeholder}
      product={product}
      tokenUsage={tokenUsage}
    />
  );
};

export default WorkflowChatInput;
